import { useEffect, useState, type ReactNode } from 'react';

import RadioTile from '@liquidweb-dev/undercurrent/RadioTile';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import CreditCardIcon from '@liquidweb-dev/undercurrent/icons/lucide/CreditCard';
import WalletIcon from '@liquidweb-dev/undercurrent/icons/lucide/Wallet';
import PayPalIcon from '@liquidweb-dev/undercurrent/icons/platform/PayPal';
import { useElements, useStripe } from '@liquidweb-dev/undercurrent/stripe';

import FadeCollapse from 'components/FadeCollapse';
import { validateValueAtLeast } from 'components/Form/HookedCurrencyField';
import HookedRadioGroup from 'components/Form/HookedRadioGroup';
import { useAcceptJs, useLoadAcceptJs } from 'modules/acceptJs';
import { creditCardValidators } from 'modules/creditCard';
import { useOpenPayPalPaymentApprovalUrl } from 'modules/queries/billing/paypal/payment/useCreate';
import useStripeCreateConfirmationToken from 'modules/queries/billing/stripe/payment/useCreateConfirmationToken';
import useBrandName from 'utility/hooks/useBrandName';
import {
	useDialogMakePaymentContext,
	type DialogMakePaymentFormValues,
} from '../dialogMakePaymentContext';
import reportPaymentFailure from '../reportPaymentFailure';
import useChosenPaymentMethod from '../useChosenPaymentMethod';
import AmountField from './AmountField';
import ChoosePaymentMethodAuthorizeDotNet from './ChoosePaymentMethodAuthorizeDotNet';
import ChoosePaymentMethodPayPal from './ChoosePaymentMethodPayPal';
import ChoosePaymentMethodStripe from './ChoosePaymentMethodStripe';
import ChooseSavedPaymentMethod from './ChooseSavedPaymentMethod';
import MakePaymentForm from './MakePaymentForm';

/** The first step while the account data behind every one of its choices is fetched */
export const StartContentSkeleton = () => (
	<Stack sx={{ gap: 300 }}>
		<Stack sx={{ gap: 400 }}>
			<Skeleton variant="rounded" sx={{ width: '100%', height: '88px' }} />

			<Stack sx={{ gap: 150 }}>
				<Stack sx={{ flexDirection: 'row', gap: 100 }}>
					<Skeleton variant="rounded" sx={{ flex: 1, height: '78px' }} />
					<Skeleton variant="rounded" sx={{ flex: 1, height: '78px' }} />
					<Skeleton variant="rounded" sx={{ flex: 1, height: '78px' }} />
				</Stack>

				<Skeleton variant="rounded" sx={{ width: '100%', height: '58px' }} />
			</Stack>
		</Stack>

		<Skeleton
			variant="rounded"
			sx={({ size }) => ({ width: '100%', height: size.lg })}
		/>
	</Stack>
);

/** One of the "how would you like to pay" options */
const PaymentModeTile = ({
	icon,
	mode,
	children,
}: {
	icon: ReactNode;
	mode: DialogMakePaymentFormValues['paymentMode'];
	children: ReactNode;
}) => {
	const { formValues } = useDialogMakePaymentContext();
	const checked = formValues.paymentMode === mode;

	return (
		<RadioTile value={mode} checked={checked} sx={{ flex: 1 }}>
			<Stack
				sx={{
					gap: 50,
					color: ({ vars }) =>
						checked ? vars.palette.uc.highlight.text.main : undefined,
				}}
			>
				{icon}
				<Typography variant="body-sm">{children}</Typography>
			</Stack>
		</RadioTile>
	);
};

/** The "how would you like to pay" options */
const PaymentModes = () => {
	const { hasPaymentMethodsError, isPayPalAllowed, paymentMethods } =
		useDialogMakePaymentContext();

	const hasSavedPayPal = paymentMethods.some(
		(method) => method.processor === 'paypal',
	);

	// an account with PayPal already saved pays with that saved method rather
	// than starting a new one-time payment
	const showPayPal = isPayPalAllowed && !hasSavedPayPal;

	// if there is nothing on file can be paid with, there is nothing to offer -
	// though methods that could not be fetched are still offered, so the panel
	// can say so and give another go at it
	const showSaved = paymentMethods.length > 0 || hasPaymentMethodsError;

	return (
		<HookedRadioGroup
			name="paymentMode"
			aria-label="How would you like to pay"
			sx={{ display: 'flex', flexDirection: 'row', gap: 100 }}
		>
			{showSaved && (
				<PaymentModeTile
					mode="saved"
					icon={
						<WalletIcon
							sx={({ size }) => ({ width: size.xS, height: size.xS })}
						/>
					}
				>
					Saved
				</PaymentModeTile>
			)}
			<PaymentModeTile
				mode="new"
				icon={
					<CreditCardIcon
						sx={({ size }) => ({ width: size.xS, height: size.xS })}
					/>
				}
			>
				Card
			</PaymentModeTile>
			{showPayPal && (
				<PaymentModeTile
					mode="paypal"
					icon={
						<PayPalIcon
							sx={({ size }) => ({ width: size.xS, height: size.xS })}
						/>
					}
				>
					Paypal
				</PaymentModeTile>
			)}
		</HookedRadioGroup>
	);
};

const StartContent = () => {
	const {
		formValues,
		isStripeAllowed,
		clearSensitiveFormValues,
		setConfirmationToken,
		setFormErrors,
		setOpaqueData,
		setStep,
	} = useDialogMakePaymentContext();

	const { chosenPaymentMethod } = useChosenPaymentMethod();

	// a card entered for one way of paying doesn't carry over to another, nor a
	// security code from one saved method to the next
	useEffect(() => {
		clearSensitiveFormValues();
	}, [
		formValues.paymentMode,
		formValues.paymentInstrumentId,
		clearSensitiveFormValues,
	]);

	const isEnteringNewCard = formValues.paymentMode === 'new';
	const isEnteringStripeCard = isEnteringNewCard && isStripeAllowed;
	const isEnteringAuthorizeDotNetCard = isEnteringNewCard && !isStripeAllowed;

	const hasLoadedAcceptJsScript = useLoadAcceptJs(
		isEnteringAuthorizeDotNetCard,
	);
	const { isSubmittingAcceptJs, submitCard, readyToSubmitAcceptJs } =
		useAcceptJs();

	const {
		openPayPalPaymentApprovalUrl,
		isLoading: isOpenPayPalPaymentApprovalUrlLoading,
	} = useOpenPayPalPaymentApprovalUrl();

	const stripe = useStripe();
	const elements = useElements();
	const { mutateAsync: createConfirmationToken } =
		useStripeCreateConfirmationToken(stripe, elements);
	// covers the card's validation as well as the token, so Continue can't be
	// activated twice while either is running
	const [isContinuingWithStripeCard, setIsContinuingWithStripeCard] =
		useState(false);

	const handleSubmitSavedMethod = () => {
		if (!chosenPaymentMethod) return;

		setStep('confirmSavedPayment');
	};

	const handleSubmitNewStripeCard = async () => {
		if (!elements) return;

		setIsContinuingWithStripeCard(true);
		try {
			// the Payment Element already shows its own field errors inline
			const { error: clientError } = await elements.submit();
			if (clientError) return;

			const confirmationToken = await createConfirmationToken();

			setConfirmationToken(confirmationToken.id);
		} catch (error) {
			reportPaymentFailure(error);

			return;
		} finally {
			setIsContinuingWithStripeCard(false);
		}

		setStep('confirmNewStripePayment');
	};

	const handleSubmitNewAuthNetCard = async () => {
		const { errors: acceptJsErrors, opaqueData } = await submitCard({
			cardNumber: formValues.cardNumber,
			expirationDate: formValues.expirationDate,
			cardCode: formValues.cardCode,
		});

		if (acceptJsErrors) {
			setFormErrors((prev) => ({ ...prev, ...acceptJsErrors }));
			return;
		}

		setOpaqueData(opaqueData);
		setStep('confirmNewAuthNetPayment');
	};

	const brandName = useBrandName();
	const handleSubmitNewPayPal = () =>
		// leaves my.lw and opens PayPal site...
		openPayPalPaymentApprovalUrl({
			requested_amount: formValues.amount,
			lineItems: [
				{ amount: formValues.amount, description: `My ${brandName} Payment` },
			],
			// ...Payment flow continues at returnURL's path
			returnURL: `${window.location.origin}/account/billing/confirm-one-time-paypal-payment`,
		});

	const handleSubmit = () => {
		if (isEnteringStripeCard) return handleSubmitNewStripeCard();
		if (isEnteringAuthorizeDotNetCard) return handleSubmitNewAuthNetCard();
		if (formValues.paymentMode === 'paypal') return handleSubmitNewPayPal();

		return handleSubmitSavedMethod();
	};

	return (
		<MakePaymentForm
			onSubmit={handleSubmit}
			validators={{
				amount: (value) => validateValueAtLeast(value, 0.5),
				// the card fields only exist while a card is entered through
				// Accept.js - Stripe collects its own
				...(isEnteringAuthorizeDotNetCard ? creditCardValidators : {}),
			}}
		>
			<Stack sx={{ gap: 400 }}>
				<AmountField />

				<PaymentModes />
			</Stack>

			{/* the panels are unmounted while collapsed - both hold a `cardCode`
			input, and two of those in one form share a value and an error */}
			<FadeCollapse
				in={formValues.paymentMode === 'saved'}
				collapseTimeout={200}
				unmountOnExit
			>
				<ChooseSavedPaymentMethod sx={{ marginTop: 150 }} />
			</FadeCollapse>

			<FadeCollapse
				in={formValues.paymentMode === 'new'}
				collapseTimeout={200}
				unmountOnExit
			>
				{isStripeAllowed ? (
					<ChoosePaymentMethodStripe
						isSubmitting={isContinuingWithStripeCard}
						readyToSubmit={Boolean(stripe && elements)}
						sx={{ marginTop: 150 }}
					/>
				) : (
					<ChoosePaymentMethodAuthorizeDotNet
						isSubmitting={isSubmittingAcceptJs}
						readyToSubmit={hasLoadedAcceptJsScript && readyToSubmitAcceptJs}
						sx={{ marginTop: 150 }}
					/>
				)}
			</FadeCollapse>

			<FadeCollapse
				in={formValues.paymentMode === 'paypal'}
				collapseTimeout={200}
				unmountOnExit
			>
				<ChoosePaymentMethodPayPal
					isSubmitting={isOpenPayPalPaymentApprovalUrlLoading}
					sx={{ marginTop: 150 }}
				/>
			</FadeCollapse>
		</MakePaymentForm>
	);
};
export default StartContent;
